/**
 * Logbook Group Settings — admin-only.
 *
 * Replaces the single on/off toggle. Each of the 21 API groups has a
 * three-state mode (off / errors / on). Quick presets at the top apply
 * a mode in bulk with optional carve-outs (e.g. "Errors except
 * Sequencing").
 *
 * Backend contract:
 *   GET  /api/logbook/groups        → catalog (key, label, tables, description)
 *   GET  /api/logbook/settings      → {enabled, default_mode, modes}
 *   PUT  /api/logbook/settings      → partial update of master/per-group
 *   PUT  /api/logbook/settings/bulk → {mode, except_groups[]}
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Database,
  ArrowsClockwise,
  CheckCircle,
  X,
  Warning,
  Lightning,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import AdminPageHeader from "@/components/AdminPageHeader";
import {
  fetchLogbookGroups,
  fetchLogbookSettings,
  updateLogbookSettings,
  bulkApplyLogbookSettings,
} from "@/lib/api";

const MODE_DEFS = [
  { key: "off",    label: "Off",    color: "slate" },
  { key: "errors", label: "Errors", color: "amber" },
  { key: "on",     label: "On",     color: "emerald" },
];

function ModePill({ value, current, onClick, disabled }) {
  const def = MODE_DEFS.find((m) => m.key === value);
  const active = value === current;
  const baseColor = def.color;
  const cls = active
    ? `bg-${baseColor}-700 text-white border-${baseColor}-700`
    : `bg-white text-${baseColor}-700 border-${baseColor}-200 hover:bg-${baseColor}-50`;
  // Tailwind JIT-safe explicit class lookup:
  const tw = {
    "slate-700":   "bg-slate-700 text-white border-slate-700",
    "slate-idle":  "bg-white text-slate-600 border-slate-200 hover:bg-slate-50",
    "amber-700":   "bg-amber-700 text-white border-amber-700",
    "amber-idle":  "bg-white text-amber-700 border-amber-200 hover:bg-amber-50",
    "emerald-700": "bg-emerald-700 text-white border-emerald-700",
    "emerald-idle":"bg-white text-emerald-700 border-emerald-200 hover:bg-emerald-50",
  };
  const flavor = active ? `${baseColor}-700` : `${baseColor}-idle`;
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onClick(value)}
      className={`px-2 py-1 rounded-sm border text-[10px] font-bold uppercase tracking-wider transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${tw[flavor]}`}
      data-testid={`mode-${value}`}
    >
      {def.label}
    </button>
  );
}

export default function LogbookSettingsPage({ user, onLogout, onLogoutAll }) {
  const [groups, setGroups] = useState([]);     // [{key,label,tables,description}]
  const [enabled, setEnabled] = useState(true);
  const [defaultMode, setDefaultMode] = useState("off");
  const [modes, setModes] = useState({});       // {key: "off"|"errors"|"on"}
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);   // group key currently being saved
  const [bulkBusy, setBulkBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [cat, st] = await Promise.all([
        fetchLogbookGroups(),
        fetchLogbookSettings(),
      ]);
      setGroups(cat.groups || []);
      setEnabled(!!st.enabled);
      setDefaultMode(st.default_mode || "off");
      setModes(st.modes || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load logbook settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const counts = useMemo(() => {
    const c = { off: 0, errors: 0, on: 0 };
    Object.values(modes).forEach((m) => { if (c[m] !== undefined) c[m] += 1; });
    return c;
  }, [modes]);

  async function setGroupMode(groupKey, nextMode) {
    if (modes[groupKey] === nextMode) return;
    setSaving(groupKey);
    try {
      const res = await updateLogbookSettings(
        { modes: { [groupKey]: nextMode } },
        user.email,
      );
      setModes(res.modes || {});
      toast.success(`${groupKey} → ${nextMode}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(null);
    }
  }

  async function toggleMaster(next) {
    if (!next && !window.confirm(
      "Disable the master logbook switch?\n\n"
      + "Nothing will be written to the logbook until it's re-enabled, "
      + "regardless of per-group modes.")) return;
    setBulkBusy(true);
    try {
      const res = await updateLogbookSettings({ enabled: next }, user.email);
      setEnabled(!!res.enabled);
      toast.success(`Master logbook ${res.enabled ? "ENABLED" : "DISABLED"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBulkBusy(false);
    }
  }

  async function applyBulk(mode, exceptGroups, label) {
    if (!window.confirm(`Apply "${label}"?\n\nThis sets the mode for every group ${exceptGroups.length ? "(except " + exceptGroups.join(", ") + ") " : ""}to "${mode}".`)) return;
    setBulkBusy(true);
    try {
      const res = await bulkApplyLogbookSettings(mode, exceptGroups, user.email);
      setModes(res.modes || {});
      toast.success(`Applied: ${label}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk apply failed");
    } finally {
      setBulkBusy(false);
    }
  }

  const presets = [
    { label: "All Off",                    mode: "off",    except: [] },
    { label: "All → Errors",               mode: "errors", except: [] },
    { label: "Errors (except Sequencing)", mode: "errors", except: ["sequencing"] },
    { label: "All On",                     mode: "on",     except: [] },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="logbook-settings-page">
      <AdminPageHeader
        title="Logbook Settings"
        user={user}
        onLogout={onLogout}
        onLogoutAll={onLogoutAll}
      />

      <main className="max-w-5xl mx-auto px-4 md:px-8 py-6 space-y-4">
        {/* Master switch + counts */}
        <div className="bg-white border border-slate-200 rounded-sm p-3 flex items-center gap-3">
          <button
            onClick={() => toggleMaster(!enabled)}
            disabled={bulkBusy}
            data-testid="master-toggle"
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-sm text-xs font-bold uppercase tracking-wider border ${
              enabled
                ? "bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100"
                : "bg-slate-100 text-slate-700 border-slate-300 hover:bg-slate-200"
            }`}
          >
            <span className={`inline-block w-2 h-2 rounded-full ${enabled ? "bg-emerald-500 animate-pulse" : "bg-slate-400"}`} />
            Master {enabled ? "Enabled" : "Disabled"}
          </button>
          <div className="text-[11px] text-slate-500 flex-1">
            {enabled
              ? "Per-group modes control what gets written. Disable to silence everything."
              : "All logging is silenced regardless of per-group modes."}
          </div>
          <div className="flex items-center gap-3 text-[11px] font-mono">
            <span className="text-slate-500">off: <b className="text-slate-700">{counts.off}</b></span>
            <span className="text-amber-700">errors: <b>{counts.errors}</b></span>
            <span className="text-emerald-700">on: <b>{counts.on}</b></span>
            <button onClick={refresh} disabled={loading} data-testid="refresh"
                    className="ml-2 inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-slate-100 hover:bg-slate-200 text-slate-700 text-[10px] font-bold uppercase">
              <ArrowsClockwise size={10} weight="bold" />
              {loading ? "…" : "Refresh"}
            </button>
          </div>
        </div>

        {/* Presets */}
        <div className="bg-white border border-slate-200 rounded-sm">
          <div className="px-3 py-2 border-b border-slate-200 flex items-center gap-2">
            <Lightning size={14} weight="bold" className="text-slate-500" />
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700">
              Quick presets
            </h2>
          </div>
          <div className="p-3 flex flex-wrap gap-2">
            {presets.map((p) => (
              <button
                key={p.label}
                onClick={() => applyBulk(p.mode, p.except, p.label)}
                disabled={bulkBusy}
                data-testid={`preset-${p.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                className="px-3 py-1.5 rounded-sm text-[11px] font-bold uppercase tracking-wider bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Per-group table */}
        <div className="bg-white border border-slate-200 rounded-sm">
          <div className="px-3 py-2 border-b border-slate-200 flex items-center gap-2">
            <Database size={14} weight="bold" className="text-slate-500" />
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700 flex-1">
              Per-group modes
            </h2>
            <span className="text-[10px] text-slate-500">
              {groups.length} groups · default: {defaultMode}
            </span>
          </div>

          {loading ? (
            <div className="p-6 text-center text-[12px] text-slate-500">Loading…</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {groups.map((g) => {
                const current = modes[g.key] || defaultMode;
                const busy = saving === g.key;
                return (
                  <li key={g.key} className="px-3 py-2.5 flex items-start gap-3"
                      data-testid={`group-row-${g.key}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2">
                        <span className="text-[13px] font-bold text-slate-800">
                          {g.label}
                        </span>
                        <code className="text-[10px] text-slate-400">{g.key}</code>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5 break-words">
                        {g.description}
                      </div>
                      {g.tables?.length > 0 && (
                        <div className="text-[10px] text-slate-400 mt-0.5 font-mono">
                          {g.tables.join(" · ")}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {MODE_DEFS.map((m) => (
                        <ModePill
                          key={m.key}
                          value={m.key}
                          current={current}
                          onClick={() => setGroupMode(g.key, m.key)}
                          disabled={busy || bulkBusy}
                        />
                      ))}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Mode legend */}
        <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-[11px] text-blue-900 leading-snug space-y-1">
          <div className="flex items-center gap-2">
            <X size={12} weight="bold" className="text-slate-600" />
            <b>Off</b> — log_action calls in this group are silently dropped.
          </div>
          <div className="flex items-center gap-2">
            <Warning size={12} weight="bold" className="text-amber-700" />
            <b>Errors</b> — only failures / warnings are written. Success is dropped.
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle size={12} weight="bold" className="text-emerald-700" />
            <b>On</b> — every event is written to the ActionLog.
          </div>
        </div>
      </main>
    </div>
  );
}
